import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '', notes: '' });
  const [packages, setPackages] = useState([]);
  const [assignPackageId, setAssignPackageId] = useState('');
  const [assignPriceOverride, setAssignPriceOverride] = useState('');
  const navigate = useNavigate();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    loadClients();
    loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/clients`, { headers });
      if (res.status === 401) { navigate('/admin/login'); return; }
      const data = await res.json();
      setClients(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadPackages = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/custom-packages`, { headers });
      const data = await res.json();
      setPackages(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const openDetail = async (clientId) => {
    setSelectedClientId(clientId);
    setDetailLoading(true);
    setClientDetail(null);
    try {
      const res = await fetch(`${backendUrl}/api/admin/clients/${clientId}`, { headers });
      setClientDetail(await res.json());
    } catch (e) { console.error(e); } finally { setDetailLoading(false); }
  };

  const closeDetail = () => {
    setSelectedClientId(null);
    setClientDetail(null);
    setAssignPackageId('');
    setAssignPriceOverride('');
  };

  const createClient = async (e) => {
    e.preventDefault();
    if (!newClient.name.trim() || !newClient.email.trim()) return;
    setInviting(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/clients`, {
        method: 'POST', headers, body: JSON.stringify(newClient),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to invite');
      setShowInviteModal(false);
      setNewClient({ name: '', email: '', phone: '', notes: '' });
      await loadClients();
      alert(`Invite sent to ${data.email}`);
    } catch (e) { alert(`Could not invite: ${e.message}`); }
    finally { setInviting(false); }
  };

  const resendInvite = async (clientId) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/clients/${clientId}/resend-invite`, { method: 'POST', headers });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      alert('Invite email re-sent.');
    } catch (e) { alert(`Could not resend: ${e.message}`); }
  };

  const setStatus = async (clientId, status) => {
    if (!window.confirm(`Set client status to \"${status}\"?`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/admin/clients/${clientId}`, {
        method: 'PATCH', headers, body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      await loadClients();
      if (selectedClientId === clientId) await openDetail(clientId);
    } catch (e) { alert(`Could not update: ${e.message}`); }
  };

  const assignPackage = async (e) => {
    e.preventDefault();
    if (!assignPackageId || !selectedClientId) return;
    try {
      const payload = { custom_package_id: assignPackageId, price_override: assignPriceOverride ? Number(assignPriceOverride) : null };
      const res = await fetch(`${backendUrl}/api/admin/clients/${selectedClientId}/assignments`, {
        method: 'POST', headers, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setAssignPackageId('');
      setAssignPriceOverride('');
      await openDetail(selectedClientId);
    } catch (e) { alert(`Could not assign: ${e.message}`); }
  };

  const removeAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this package from the client?')) return;
    try {
      const res = await fetch(`${backendUrl}/api/admin/assignments/${assignmentId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Failed');
      await openDetail(selectedClientId);
    } catch (e) { alert(`Could not remove: ${e.message}`); }
  };

  const statusBadge = (status) => {
    const map = {
      invited: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      deactivated: 'bg-gray-200 text-gray-700',
    };
    return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>;
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Clients</h2>
          <p className="text-sm text-gray-500 mt-1">Invite corporate & repeat clients to your private portal.</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          data-testid="invite-client-btn"
          className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium"
        >
          <i className="ri-user-add-line mr-1"></i> Invite Client
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <i className="ri-user-star-line text-4xl text-gray-300"></i>
          <p className="mt-3 text-gray-500">No clients yet. Invite your first one.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Phone</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clients.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                  <td className="px-4 py-3 text-gray-700">{c.email}</td>
                  <td className="px-4 py-3 text-gray-500">{c.phone || '—'}</td>
                  <td className="px-4 py-3">{statusBadge(c.status)}</td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => openDetail(c.id)} data-testid={`view-client-${c.id}`} className="text-blue-600 hover:text-blue-800 text-xs font-medium">View</button>
                    {c.status === 'invited' && (
                      <button onClick={() => resendInvite(c.id)} className="text-teal-600 hover:text-teal-800 text-xs font-medium">Resend Invite</button>
                    )}
                    {c.status !== 'deactivated' ? (
                      <button onClick={() => setStatus(c.id, 'deactivated')} className="text-red-600 hover:text-red-800 text-xs font-medium">Deactivate</button>
                    ) : (
                      <button onClick={() => setStatus(c.id, 'active')} className="text-green-600 hover:text-green-800 text-xs font-medium">Reactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Invite Client</h3>
              <button onClick={() => setShowInviteModal(false)} className="text-gray-400 text-2xl">×</button>
            </div>
            <form onSubmit={createClient} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                <input type="text" required value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" data-testid="invite-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" required value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" data-testid="invite-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                <textarea value={newClient.notes} onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="pt-2 flex gap-2">
                <button type="submit" disabled={inviting} data-testid="invite-submit-btn" className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white py-2 rounded-lg font-medium">{inviting ? 'Sending…' : 'Send Invite'}</button>
                <button type="button" onClick={() => setShowInviteModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedClientId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeDetail}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-900">Client Details</h3>
              <button onClick={closeDetail} className="text-gray-400 text-2xl">×</button>
            </div>
            <div className="p-6 space-y-6">
              {detailLoading ? (
                <div className="text-center py-8 text-gray-500">Loading…</div>
              ) : clientDetail ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div><div className="text-xs text-gray-500">Name</div><div className="font-medium">{clientDetail.name}</div></div>
                    <div><div className="text-xs text-gray-500">Email</div><div className="font-medium">{clientDetail.email}</div></div>
                    <div><div className="text-xs text-gray-500">Phone</div><div className="font-medium">{clientDetail.phone || '—'}</div></div>
                    <div><div className="text-xs text-gray-500">Status</div><div>{statusBadge(clientDetail.status)}</div></div>
                  </div>
                  {clientDetail.notes && (
                    <div><div className="text-xs text-gray-500">Notes</div><div className="text-sm text-gray-700 whitespace-pre-wrap">{clientDetail.notes}</div></div>
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Assigned Packages</h4>
                    {(clientDetail.assignments || []).length === 0 ? (
                      <p className="text-sm text-gray-500 mb-3">No packages assigned yet.</p>
                    ) : (
                      <div className="space-y-2 mb-4">
                        {clientDetail.assignments.map((a) => {
                          const pkg = a.custom_packages || {};
                          const price = a.price_override != null ? a.price_override : pkg.default_price;
                          return (
                            <div key={a.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                              <div>
                                <div className="font-medium text-gray-900">{pkg.name}</div>
                                <div className="text-xs text-gray-500">A${Number(price || 0).toFixed(2)}{a.price_override != null && <span className="ml-2 text-teal-600 font-medium">(override)</span>}</div>
                              </div>
                              <div className="flex items-center gap-3">
                                <button onClick={() => navigate(`/admin/clients/${selectedClientId}/book/${a.id}`)} data-testid={`book-for-client-${a.id}`} className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg"><i className="ri-calendar-check-line mr-1"></i>Book for client</button>
                                <button onClick={() => removeAssignment(a.id)} className="text-red-600 text-xs font-medium">Remove</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <form onSubmit={assignPackage} className="flex gap-2 items-end bg-teal-50 border border-teal-200 rounded-lg p-4">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-600 mb-1">Custom Package</label>
                        <select value={assignPackageId} onChange={(e) => setAssignPackageId(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                          <option value="">Select a package…</option>
                          {packages.map((p) => (
                            <option key={p.id} value={p.id}>{p.name} (A${p.default_price})</option>
                          ))}
                        </select>
                      </div>
                      <div className="w-40">
                        <label className="block text-xs text-gray-600 mb-1">Price Override (opt.)</label>
                        <input type="number" step="0.01" min="0" value={assignPriceOverride} onChange={(e) => setAssignPriceOverride(e.target.value)} placeholder="e.g. 450" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      </div>
                      <button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-lg font-medium">Assign</button>
                    </form>
                    {packages.length === 0 && (
                      <p className="text-xs text-gray-500 mt-2">No custom packages exist yet. Create some under “Custom Packages” first.</p>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Bookings</h4>
                    {(clientDetail.bookings || []).length === 0 ? (
                      <p className="text-sm text-gray-500">No bookings yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {clientDetail.bookings.map((b) => (
                          <div key={b.id} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-2 text-sm">
                            <div>
                              <span className="font-medium text-gray-900">{b.booking_date}</span>
                              <span className="text-gray-500 ml-2">{b.booking_time}</span>
                              <span className="text-gray-400 ml-2">· {b.property_address || '—'}</span>
                            </div>
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{b.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-gray-500">Could not load client.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
